import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/analyse-annonce/config`)
      .then((r) => setCalendlyUrl(r.data?.calendly_url || null))
      .catch(() => setCalendlyUrl(null))
      .finally(() => setConfigLoaded(true));
  }, []);

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#302f2c]">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="text-center mb-16">
          <h2 className="font-black text-5xl md:text-7xl leading-tight text-[#d9fb06] uppercase mb-6">
            Contactez-nous
          </h2>
          <p className="text-xl text-[#888680] max-w-3xl mx-auto">
            Prêt à faire le meilleur achat de votre vie ?
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-3xl font-bold text-[#d9fb06] mb-6 uppercase">
              Parlons de votre projet
            </h3>
            <p className="text-[#dfddd6] text-lg mb-8 leading-relaxed">
              Réservez votre appel découverte gratuit. Nous discuterons de vos besoins, de votre budget et de la meilleure façon de vous accompagner.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start space-x-4">
                <div className="bg-[#d9fb06] p-3 rounded-full">
                  <Calendar className="text-[#1a1c1b]" size={24} />
                </div>
                <div>
                  <h4 className="text-[#d9fb06] font-bold mb-1 uppercase">Réservation en ligne</h4>
                  <p className="text-[#888680]">Choisissez votre créneau via Calendly</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#d9fb06] p-3 rounded-full">
                  <Mail className="text-[#1a1c1b]" size={24} />
                </div>
                <div>
                  <h4 className="text-[#d9fb06] font-bold mb-1 uppercase">Email</h4>
                  <p className="text-[#888680]">contact@validatedboat.fr</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#d9fb06] p-3 rounded-full">
                  <Phone className="text-[#1a1c1b]" size={24} />
                </div>
                <div>
                  <h4 className="text-[#d9fb06] font-bold mb-1 uppercase">Téléphone</h4>
                  <p className="text-[#888680]">Sur rendez-vous uniquement</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#d9fb06] p-3 rounded-full">
                  <MapPin className="text-[#1a1c1b]" size={24} />
                </div>
                <div>
                  <h4 className="text-[#d9fb06] font-bold mb-1 uppercase">Zone d'intervention</h4>
                  <p className="text-[#888680]">France métropolitaine (déplacements possibles)</p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-6">
              <h4 className="text-[#d9fb06] font-bold mb-3 uppercase text-lg">À quoi s'attendre lors de l'appel ?</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#d9fb06] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-[#dfddd6] text-sm">Discussion de votre projet et de vos besoins</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#d9fb06] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-[#dfddd6] text-sm">Évaluation de votre budget et critères</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#d9fb06] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-[#dfddd6] text-sm">Recommandations personnalisées</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#d9fb06] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-[#dfddd6] text-sm">Proposition d'accompagnement adapté</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Calendly Widget — driven by admin config */}
          <div className="bg-[#1a1c1b] border-2 border-[#d9fb06] rounded-lg overflow-hidden" data-testid="calendly-container">
            {calendlyUrl ? (
              <InlineWidget
                url={calendlyUrl}
                styles={{ height: '700px', minWidth: '320px' }}
              />
            ) : (
              <div className="p-8 md:p-12 text-center" data-testid="calendly-fallback">
                <Calendar className="mx-auto text-[#d9fb06] mb-4" size={48} />
                <h3 className="text-xl md:text-2xl font-bold text-[#d9fb06] uppercase mb-3">
                  {configLoaded ? "Réservation d'appel indisponible" : "Chargement du planning…"}
                </h3>
                <p className="text-[#dfddd6] mb-6 text-sm">
                  {configLoaded
                    ? "La prise de rendez-vous en ligne est temporairement désactivée. Contactez-moi directement pour convenir d'un créneau."
                    : "Merci de patienter…"}
                </p>
                {configLoaded && (
                  <a
                    href="mailto:contact@validatedboat.fr?subject=Demande d'appel découverte"
                    data-testid="calendly-fallback-email"
                    className="inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 text-sm font-bold uppercase transition-colors"
                  >
                    <Mail size={16} /> Écrire pour un appel
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;